import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';

import { AUTH_SERVER_URL } from '../../config';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { Users } from '../../users/models/user';
import { OrganizationDetails } from '../models/organization';
import { OrderBy } from '../../shared/utils/constant';
import { OrganizationUserInfo } from '../models/organization-user-info';

const ORG_URL = `${ AUTH_SERVER_URL }/v2/organizations:external`;

@Injectable({ providedIn: 'root' })
export class AddSubOrganizationService {
	organizationName: Observable<string>;
	vatNumber: Observable<string>;
	userFirstName: Observable<string>;
	userLastName: Observable<string>;
	email: Observable<string>;
	orgId: Observable<string>;
	isNewSubOrg: Observable<boolean>;
	userId: Observable<string>;
	isAnyOneOrgSelected: Observable<boolean>;
	isAnyOneUserSelected: Observable<boolean>;
	vatNumberExistsCheck: Observable<boolean>;
	nodeSelectedMatchingOrgs: Observable<number | undefined>;
	currentSelectedUser: Observable<Users | undefined>;
	currentSelectedOrg: Observable<OrganizationDetails | undefined>;
	private readonly organizationNameSubject = new BehaviorSubject(<string>'');
	private readonly vatNumberSubject = new BehaviorSubject(<string>'');
	private readonly userLastNameSubject = new BehaviorSubject(<string>'');
	private readonly userFirstNameSubject = new BehaviorSubject(<string>'');
	private readonly emailSubject = new BehaviorSubject(<string>'');
	private readonly orgIdSubject = new BehaviorSubject(<string>'');
	private readonly isNewSubOrgSubject = new BehaviorSubject(<boolean>false);
	private readonly vatNumberExistsSubject = new BehaviorSubject(<boolean>false);
	private readonly userIdSubject = new BehaviorSubject(<string>'');
	private readonly selectedUserSubject = new BehaviorSubject(<Users | undefined>{});
	private readonly selectedOrgSubject = new BehaviorSubject(<OrganizationDetails | undefined>{});
	private readonly isAnyOneOrgSelectedSubject = new BehaviorSubject(<boolean>false);
	private readonly isAnyOneUserSelectedSubject = new BehaviorSubject(<boolean>false);
	private readonly nodeSelectedMatchingOrgsSubject = new BehaviorSubject(<number | undefined>undefined);
	private readonly nodeSelectedUsersSubject = new BehaviorSubject(<number | undefined>undefined);
	constructor(
		private readonly http: HttpClient,
		private readonly router: Router
	) {
		this.organizationName = this.organizationNameSubject.asObservable();
		this.vatNumber = this.vatNumberSubject.asObservable();
		this.userFirstName = this.userFirstNameSubject.asObservable();
		this.userLastName = this.userLastNameSubject.asObservable();
		this.email = this.emailSubject.asObservable();
		this.orgId = this.orgIdSubject.asObservable();
		this.isNewSubOrg = this.isNewSubOrgSubject.asObservable();
		this.userId = this.userIdSubject.asObservable();
		this.isAnyOneOrgSelected = this.isAnyOneOrgSelectedSubject.asObservable();
		this.nodeSelectedMatchingOrgs = this.nodeSelectedMatchingOrgsSubject.asObservable();
		this.vatNumberExistsCheck = this.vatNumberExistsSubject.asObservable();
		this.isAnyOneUserSelected = this.isAnyOneUserSelectedSubject.asObservable();
		this.currentSelectedUser = this.selectedUserSubject.asObservable();
		this.currentSelectedOrg = this.selectedOrgSubject.asObservable();
	}

	updateUserId(userId: string) {
		this.userIdSubject.next(userId);
	}

	updateOrganizationName(organizationName: string) {
		this.organizationNameSubject.next(organizationName);
	}

	updateVatNumber(vatNumber: string) {
		this.vatNumberSubject.next(vatNumber);
	}

	updateFirstName(firstName: string) {
		this.userFirstNameSubject.next(firstName);
	}

	updateLastName(lastName: string) {
		this.userLastNameSubject.next(lastName);
	}

	updateEmail(email: string) {
		this.emailSubject.next(email);
	}

	updateOrgId(orgId: string) {
		this.orgIdSubject.next(orgId);
	}

	updateIsNewSubOrg(isNewSubOrg: boolean) {
		this.isNewSubOrgSubject.next(isNewSubOrg);
	}

	updateVatNumberExists(vatNumberExists: boolean) {
		this.vatNumberExistsSubject.next(vatNumberExists);
	}

	updateNodeSelectedMatchingOrgs(node: number | undefined) {
		this.nodeSelectedMatchingOrgsSubject.next(node);
	}

	updateNodeSelectedUsers(node: number | undefined) {
		this.nodeSelectedUsersSubject.next(node);
	}

	updateIsAnyOneOrgChoiceSelected(isAnyOneOrgSelected: boolean) {
		this.isAnyOneOrgSelectedSubject.next(isAnyOneOrgSelected);
	}

	updateIsAnyOneUserChoiceSelected(isAnyOneUserSelected: boolean) {
		this.isAnyOneUserSelectedSubject.next(isAnyOneUserSelected);
	}

	getCurrentRoute(): string {
		return this.router.url;
	}

	async vatNumberExists(vatNumber: string) {
		const url = new URL(`${ ORG_URL }/vatNumber/${ vatNumber }`);
		return firstValueFrom(this.http.get<boolean>(url.toString()));
	}

	async getAdminUsers(organizationId: number) {
		const url = new URL(`${ ORG_URL }/${ organizationId }/adminusers`);
		return firstValueFrom(this.http.get<OrganizationUserInfo[]>(url.toString()));
	}

	async addNewSubOrg(orgId: number, addSubOrgInfo: OrganizationDetails) {
		const url = new URL(`${ ORG_URL }/${ orgId }:addSubOrg:new`);
		await firstValueFrom(this.http.post(url.toString(), addSubOrgInfo));
	}

	async addSubOrg(orgId: number, addSubOrgInfo: OrganizationDetails) {
		const url = new URL(`${ ORG_URL }/${ orgId }:addSubOrg`);
		await firstValueFrom(this.http.post(url.toString(), addSubOrgInfo));
	}

	async findUsersForSubOrg(
		fields: string[],
		filter: string,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof Users>[] | OrderBy<keyof Users>
	) {
		const url = new URL(`${ ORG_URL }/users:suborg`);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		params.append('filter', filter);
		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));
		return firstValueFrom(this.http.get<PaginatedResult<Users>>(url.toString()));
	}

	async getVisibleSubOrganizations(vatNumber: string, organizationName: string) {
		const url = new URL(`${ ORG_URL }/subOrgs/${ vatNumber }/${ organizationName }`);
		return firstValueFrom(this.http.get<OrganizationDetails[]>(url.toString()));
	}

	setSelectedUser(currentSelectedUser: Users | undefined) {
		this.selectedUserSubject.next(currentSelectedUser);
	}

	setSelectedOrg(currentSelectedOrg: OrganizationDetails | undefined) {
		this.selectedOrgSubject.next(currentSelectedOrg);
	}
}
